/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import PropTypes from 'prop-types'
import React, {
    Fragment,
    forwardRef,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react'
import {Link as RouteLink} from 'react-router-dom'

// Components
import {
    AspectRatio,
    Box,
    Container,
    Fade,
    Flex,
    Image,
    Popover,
    PopoverBody,
    PopoverContent,
    PopoverTrigger,
    Stack,
    Text,
    useDisclosure,
    useMediaQuery,
    useTheme
} from '@salesforce/retail-react-app/app/components/shared/ui'
import Link from '../link'

import {ChevronDownIcon} from '@salesforce/retail-react-app/app/components/icons'
import MegaNavLinks from '../mega-nav-links'
import Overlay from '../overlay'
import MenuItemSkeleton from './partials/menu-item-skeleton'
import './list-menu.scss'
import {contentfulMediaFormatter} from '../../utils'

const ChevronIconTrigger = forwardRef(function ChevronIconTrigger(props, ref) {
    return (
        <Box {...props} ref={ref}>
            <ChevronDownIcon />
        </Box>
    )
})

const ListMenuTrigger = ({item, isOpen, onOpen, onClose}) => {
    const theme = useTheme()
    const {baseStyle} = theme.components.ListMenu

    const keyMap = {
        Escape: () => onClose(),
        Enter: () => onOpen()
    }

    const buttonStyles = {
        minHeight: '37.50px',
        padding: '0 !important',
        outline: '0 !important',
        outlineOffset: '0 !important',
        lineHeight: '18.75px',
        height: 'auto',
        display: 'flex',
        alignItems: 'center',
        background: item?.theme?.backgroundColor ?? 'none',
        backgroundColor: item?.theme?.backgroundColor ?? 'none',
        _hover: {
            background: item?.theme?.backgroundColor ?? 'none',
            backgroundColor: item?.theme?.backgroundColor ?? 'none',
            textDecoration: 'none'
        }
    }

    // TODO: Add proper condition to check if the item is heading or Link
    //  TODO: Add title case for text
    return (
        <Box {...baseStyle.listMenuTriggerContainer}>
            <Link
                type={item.type || 'tertiary'}
                {...(item.url || item.externalUrl
                    ? {
                          buttonStyles,
                          url: item.url,
                          isExternal: item.isExternal
                      }
                    : {
                          buttonStyles
                      })}
                onMouseOver={onOpen}
                {...baseStyle.listMenuTriggerLink}
                {...(isOpen ? baseStyle.listMenuTriggerLinkActive : {})}
                _active={{background: 'unset'}}
            >
                <Text fontSize="19px">
                    {item.url || item.externalUrl ? item.text : item.heading}
                </Text>
            </Link>
            <PopoverTrigger>
                <Link
                    {...item}
                    as={RouteLink}
                    onMouseOver={onOpen}
                    onKeyDown={(e) => {
                        keyMap[e.key]?.(e)
                    }}
                    {...baseStyle.listMenuTriggerLinkIcon}
                >
                    <Fade in={true}>
                        <ChevronIconTrigger {...baseStyle.selectedButtonIcon} />
                    </Fade>
                </Link>
            </PopoverTrigger>
        </Box>
    )
}

ListMenuTrigger.propTypes = {
    name: PropTypes.string,
    isOpen: PropTypes.bool,
    onOpen: PropTypes.func,
    onClose: PropTypes.func,
    item: PropTypes.shape({
        url: PropTypes.string,
        externalUrl: PropTypes.string,
        text: PropTypes.string,
        type: PropTypes.string,
        isExternal: PropTypes.bool,
        heading: PropTypes.string,
        theme: PropTypes.shape({
            backgroundColor: PropTypes.string
        })
    })
}

const ListMenuContent = ({items, media, onClose}) => {
    const theme = useTheme()
    const {baseStyle} = theme.components.ListMenu

    return (
        <PopoverContent
            data-testid="popover-menu"
            {...baseStyle.popoverContent}
        >
            <PopoverBody>
                <Container
                    as={Stack}
                    {...baseStyle.popoverContainer}
                    display="flex"
                    flexDirection="row"
                >
                    <MegaNavLinks items={items} onClose={onClose} />
                    <Flex
                        width="50%"
                        gap="17px"
                        alignItems="start"
                        justifyContent="flex-end"
                    >
                        {media?.url && (
                            <Flex
                                width="100%"
                                alignItems="center"
                                justifyContent="flex-end"
                                overflow="hidden"
                            >
                                <AspectRatio
                                    ratio={2 / 3}
                                    width="100%"
                                    maxHeight="330px"
                                    height={{base: '40vh', md: '100%'}}
                                >
                                    <Image
                                        width="100%"
                                        borderRadius="24px"
                                        data-testid={'footer-banner'}
                                        src={media?.url}
                                        ignoreFallback={true}
                                        alt={media?.url}
                                    />
                                </AspectRatio>
                            </Flex>
                        )}
                    </Flex>
                </Container>
            </PopoverBody>
        </PopoverContent>
    )
}
ListMenuContent.propTypes = {
    items: PropTypes.array,
    media: PropTypes.object,
    onClose: PropTypes.func,
    header: PropTypes.object,
    top: PropTypes.number
}

const ListMenuPopover = ({items, item}) => {
    const [isDesktop] = useMediaQuery('(min-width: 992px)')
    const initialFocusRef = useRef()
    const {isOpen, onClose, onOpen} = useDisclosure()
    const timeoutRef = useRef(null)

    const handleMouseLeave = () =>
        (timeoutRef.current = setTimeout(onClose, 200))

    const handleMouseEnter = () => clearTimeout(timeoutRef.current)

    useEffect(() => {
        return () => clearTimeout(timeoutRef.current) // Cleanup timeout on unmount
    }, [])

    const hasItems = useMemo(() => items?.length > 0, [items])
    const media = useMemo(() => {
        const assets = contentfulMediaFormatter(item?.media)
        return !isDesktop && assets?.mobile?.url
            ? assets?.mobile
            : assets?.desktop
    }, [item, isDesktop])

    return (
        <Box onMouseLeave={handleMouseLeave} onMouseEnter={handleMouseEnter}>
            {isOpen && hasItems && <Overlay onMouseEnter={handleMouseLeave} />}
            <Popover
                isLazy
                placement={'bottom-start'}
                initialFocusRef={initialFocusRef}
                onOpen={onOpen}
                isOpen={isOpen}
                variant="fullWidth"
            >
                <Fragment>
                    <ListMenuTrigger
                        item={item}
                        isOpen={isOpen}
                        onOpen={onOpen}
                        onClose={onClose}
                    />
                    {hasItems && (
                        <ListMenuContent
                            items={items}
                            media={media}
                            onClose={onClose}
                        />
                    )}
                </Fragment>
            </Popover>
        </Box>
    )
}

ListMenuPopover.propTypes = {
    items: PropTypes.array,
    item: PropTypes.shape({
        media: PropTypes.object,
        url: PropTypes.string,
        text: PropTypes.string,
        name: PropTypes.string,
        type: PropTypes.string,
        isExternal: PropTypes.bool
    }),
    name: PropTypes.string,
    header: PropTypes.object,
    top: PropTypes.number
}

/**
 * This is the navigation component used for desktop devices. Holds the site navigation,
 * providing users with a way to access all product categories and other important pages.
 * The submenus are open when the user moves the mouse over the trigger and for A11y when
 * users use the keyboard Tab key to focus over the chevron icon and press Enter.
 *
 * @param maxColumns The maximum number of columns that we want to use per row inside the ListMenu.
 * @param root
 */
const ListMenu = ({header}) => {
    const theme = useTheme()
    const menuItems = header?.menu?.items ?? []
    const {baseStyle} = theme.components.ListMenu
    const [ariaBusy, setAriaBusy] = useState(true)

    useEffect(() => {
        setAriaBusy(false)
    }, [])

    return (
        <nav
            id="list-menu"
            aria-label="main"
            aria-live="polite"
            aria-busy={ariaBusy}
            aria-atomic="true"
        >
            <Flex {...baseStyle.container} justifyContent="center">
                {menuItems?.length ? (
                    <Stack
                        direction={'row'}
                        spacing={0}
                        {...baseStyle.stackContainer}
                    >
                        {menuItems?.map?.(({sys, fields: item}) => (
                            <Box key={sys?.id}>
                                <ListMenuPopover
                                    item={item}
                                    items={item?.items ?? []}
                                />
                            </Box>
                        ))}
                    </Stack>
                ) : (
                    <Box display="flex">
                        <MenuItemSkeleton />
                        <MenuItemSkeleton />
                        <MenuItemSkeleton />
                        <MenuItemSkeleton />
                        <MenuItemSkeleton />
                    </Box>
                )}
            </Flex>
        </nav>
    )
}

ListMenu.displayName = 'ListMenu'

ListMenu.propTypes = {
    header: PropTypes.object,
    top: PropTypes.number
}

export default ListMenu
